// Location helpers shared by the talent-facing pages (my calendar, invitations, agency
// management). Two different things are called "location" in this app and both show up on
// those pages:
//
//   booking location — the studio a job runs at. The backend JOINs the `locations` table and
//                      sends { id, name, city, short }; older/partial payloads sometimes send
//                      a plain string instead.
//   talent locations — the studios a talent works at, stored on the talent as an array of
//                      short codes (sometimes as objects with a `short` field).
//
// Everything returns short codes for filtering (that is what talent locations hold, so the two
// can be compared) and a longer label for display.

/** Short code of the studio a booking runs at — "" when the booking has none. */
export const bookingLocationShort = (booking) => {
  const loc = booking?.location;
  if (loc && typeof loc === 'object') return String(loc.short || loc.name || '').trim();
  if (typeof loc === 'string') return loc.trim();
  return '';
};

/**
 * Compact studio label for a row, e.g. "OSB · Düsseldorf".
 *
 * Note the `locations` table stores the STREET ADDRESS in `name`
 * (" Am Rosenkothen 2, 40880 Ratingen"), so the short code plus city is what reads well in a
 * list; the address itself belongs in bookingLocationFull.
 */
export const bookingLocationLabel = (booking, fallback = '—') => {
  const loc = booking?.location;
  if (loc && typeof loc === 'object') {
    const short = String(loc.short || '').trim();
    const city = String(loc.city || '').trim();
    if (short && city) return `${short} · ${city}`;
    if (short) return short;
    if (city) return city;
    const name = String(loc.name || '').trim();
    if (name) return name;
  }
  if (typeof loc === 'string' && loc.trim()) return loc.trim();
  return fallback;
};

/**
 * Studio with its address — for detail views and tooltips, where a talent actually needs to
 * know where to turn up: "OSB · Düsseldorf — Am Rosenkothen 2, 40880 Ratingen".
 */
export const bookingLocationFull = (booking, fallback = '—') => {
  const loc = booking?.location;
  const label = bookingLocationLabel(booking, '');
  const address = loc && typeof loc === 'object' ? String(loc.name || '').trim() : '';
  if (label && address && address !== label) return `${label} — ${address}`;
  return label || address || fallback;
};

/**
 * Short codes of the studios a talent works at. Handles both shapes the API returns —
 * ["EB", "TR"] and [{ short: "EB" }, …] — and always yields strings.
 */
export const talentLocationShorts = (talent) => {
  const raw = talent?.locations;
  if (!Array.isArray(raw)) {
    if (typeof raw === 'string' && raw.trim()) return [raw.trim()];
    return [];
  }
  return raw
    .map((loc) => {
      if (typeof loc === 'string') return loc.trim();
      if (loc && typeof loc === 'object') return String(loc.short || loc.name || '').trim();
      return '';
    })
    .filter(Boolean);
};

/** "EB, TR" for a talent row; the dash when the talent has no location on file. */
export const talentLocationLabel = (talent, fallback = '—') => {
  const shorts = talentLocationShorts(talent);
  return shorts.length > 0 ? shorts.join(', ') : fallback;
};

/**
 * react-select options built from whatever locations actually occur in the given rows, so a
 * filter never offers a studio that would return nothing. `getter` yields one short code (or
 * an array of them) per row.
 */
export const locationOptionsFrom = (rows, getter) => {
  const shorts = new Set();
  (rows || []).forEach((row) => {
    const value = getter(row);
    (Array.isArray(value) ? value : [value]).forEach((v) => {
      const s = v != null ? String(v).trim() : '';
      if (s && s !== '—') shorts.add(s);
    });
  });
  return [...shorts].sort((a, b) => a.localeCompare(b)).map((s) => ({ value: s, label: s }));
};
